/**
 * EvaluationOverview.jsx — Phase 2C Assessment Evaluation Overview
 *
 * Landing page for the Evaluation module. Shows summary stats and
 * quick-links to each C1–C6 workflow stage.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  UserPlus,
  ClipboardList,
  Scale,
  BarChart3,
  CheckCircle2,
  BookOpen,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { EvaluationShell } from "../components/evaluation/EvaluationShell";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw Object.assign(
      new Error(body?.detail?.message || body?.detail || r.statusText),
      { status: r.status }
    );
  }
  return r.json();
}

const WORKFLOW_STAGES = [
  {
    id: "allocation",
    label: "Marker Allocation",
    desc: "Assign primary, second, and moderator roles. SOD-enforced.",
    icon: UserPlus,
    href: "/assessment-evaluation/marker-allocation",
    accent: "indigo",
    phase: "C1",
  },
  {
    id: "marking",
    label: "Marking",
    desc: "Faculty score against rubric criteria. Autosave. Submit to lock.",
    icon: ClipboardList,
    href: "/assessment-evaluation/marking",
    accent: "sky",
    phase: "C2",
  },
  {
    id: "moderation",
    label: "Second Mark & Moderation",
    desc: "Independent second marking. Threshold comparison. Moderator resolution.",
    icon: Scale,
    href: "/assessment-evaluation/moderation",
    accent: "amber",
    phase: "C3",
  },
  {
    id: "normalization",
    label: "Normalization",
    desc: "Statistical scaling when policy enables it. Frozen population.",
    icon: BarChart3,
    href: "/assessment-evaluation/normalization",
    accent: "purple",
    phase: "C4",
  },
  {
    id: "ratification",
    label: "Ratification",
    desc: "Coordinator prepares; authorised ratifier approves the frozen manifest.",
    icon: CheckCircle2,
    href: "/assessment-evaluation/ratification",
    accent: "emerald",
    phase: "C5",
  },
  {
    id: "gradebook",
    label: "Gradebook Handoff",
    desc: "Idempotent Phase 1E handoff. Student visible only after publication.",
    icon: BookOpen,
    href: "/assessment-evaluation/ratification",
    accent: "rose",
    phase: "C6",
  },
];

const ACCENT_MAP = {
  indigo: { card: "border-indigo-800/40 hover:border-indigo-600/60", badge: "bg-indigo-900/30 text-indigo-300 border-indigo-800/40", icon: "text-indigo-400" },
  sky:    { card: "border-sky-800/40 hover:border-sky-600/60",       badge: "bg-sky-900/30 text-sky-300 border-sky-800/40",          icon: "text-sky-400" },
  amber:  { card: "border-amber-800/40 hover:border-amber-600/60",   badge: "bg-amber-900/30 text-amber-300 border-amber-800/40",    icon: "text-amber-400" },
  purple: { card: "border-purple-800/40 hover:border-purple-600/60", badge: "bg-purple-900/30 text-purple-300 border-purple-800/40", icon: "text-purple-400" },
  emerald:{ card: "border-emerald-800/40 hover:border-emerald-600/60",badge: "bg-emerald-900/30 text-emerald-300 border-emerald-800/40",icon: "text-emerald-400" },
  rose:   { card: "border-rose-800/40 hover:border-rose-600/60",     badge: "bg-rose-900/30 text-rose-300 border-rose-800/40",      icon: "text-rose-400" },
};

export default function EvaluationOverview() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/evaluation/cases")
      .then((d) => setCases(d.cases || []))
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  }, []);

  const byStatus = cases.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <EvaluationShell title="Assessment Evaluation" breadcrumb="Overview">
      {/* Hero strip */}
      <div className="mb-8 rounded-xl border border-indigo-800/30 bg-indigo-950/20 px-6 py-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-indigo-900/40 border border-indigo-700/40 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-5 h-5 text-indigo-400" />
        </div>
        <div>
          <div className="text-sm font-semibold text-indigo-200 mb-1">Phase 2C — Evaluation Active</div>
          <div className="text-xs text-slate-400 max-w-xl">
            Full evaluation lifecycle: marker allocation → criterion scoring → second-mark / moderation
            → normalization → ratification → gradebook handoff. All gates are separation-of-duties enforced.
          </div>
        </div>
        {loading ? (
          <RefreshCw className="w-4 h-4 text-slate-500 animate-spin ml-auto mt-1" />
        ) : (
          <span className="ml-auto text-xs text-slate-500 mt-1 whitespace-nowrap">
            {cases.length} case{cases.length !== 1 ? "s" : ""} in scope
          </span>
        )}
      </div>

      {/* Status pills */}
      {!loading && cases.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="eval-status-summary">
          {Object.entries(byStatus).map(([status, count]) => (
            <span
              key={status}
              className="px-3 py-1 rounded-full text-xs font-medium border border-slate-700 bg-slate-800/60 text-slate-300"
            >
              {status.replace(/_/g, " ")} · {count}
            </span>
          ))}
        </div>
      )}

      {/* Workflow stage cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="eval-stage-grid">
        {WORKFLOW_STAGES.map((stage) => {
          const colors = ACCENT_MAP[stage.accent];
          const Icon = stage.icon;
          return (
            <Link
              key={stage.id}
              to={stage.href}
              data-testid={`eval-stage-${stage.id}`}
              className={`group block rounded-xl border bg-slate-900/60 p-5 transition-all duration-150 ${colors.card}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg bg-slate-800/80 border border-slate-700/60 flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${colors.icon}`} />
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${colors.badge}`}>
                  {stage.phase}
                </span>
              </div>
              <div className="text-sm font-semibold text-slate-100 mb-1">{stage.label}</div>
              <div className="text-xs text-slate-500 mb-3 leading-relaxed">{stage.desc}</div>
              <div className={`flex items-center gap-1 text-xs font-medium ${colors.icon} group-hover:gap-2 transition-all`}>
                Open <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          );
        })}
      </div>
    </EvaluationShell>
  );
}
